import React, { useState, useRef, useEffect } from 'react';

type SelectSize = 'sm' | 'md';

interface SelectOption {
  value: string;
  label: string;
  group?: string;
}

interface SelectProps extends Omit<React.SelectHTMLAttributes<HTMLSelectElement>, 'size' | 'onChange' | 'value'> {
  options: SelectOption[];
  value?: string;
  onChange?: (event: { target: { value: string; name?: string } }) => void;
  selectSize?: SelectSize;
  placeholder?: string;
}

const Select: React.FC<SelectProps> = ({
  options,
  value,
  onChange,
  selectSize = 'md',
  placeholder,
  className = '',
  disabled,
  name,
  ...props
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  
  const sizeClass = selectSize === 'sm' ? 'px-2 py-1.5 text-xs' : 'px-3 py-2 text-sm';
  
  const selectedOption = options.find(opt => opt.value === value);
  const displayValue = selectedOption ? selectedOption.label : placeholder || 'Select...';

  const hasGroups = options.some((opt) => opt.group);
  const groupedOptions: Record<string, SelectOption[]> = {};
  const ungroupedOptions: SelectOption[] = [];

  if (hasGroups) {
    options.forEach((option) => {
      if (option.group) {
        if (!groupedOptions[option.group]) {
          groupedOptions[option.group] = [];
        }
        groupedOptions[option.group].push(option);
      } else {
        ungroupedOptions.push(option);
      }
    });
  }

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelect = (optionValue: string) => {
    if (onChange) {
      onChange({ target: { value: optionValue, name } });
    }
    setIsOpen(false);
  };

  return (
    <div className={`relative ${className}`} ref={containerRef}>
      <button
        type="button"
        disabled={disabled}
        onClick={() => setIsOpen(!isOpen)}
        className={`
          w-full flex items-center justify-between border border-[var(--border-strong)] bg-[var(--surface-1)] text-[var(--app-text)] font-bold tracking-widest uppercase transition-all shadow-[2px_2px_0px_0px_var(--border-strong)]
          ${sizeClass}
          ${disabled ? 'opacity-50 cursor-not-allowed' : 'hover:-translate-x-px hover:-translate-y-px hover:shadow-[3px_3px_0px_0px_var(--border-strong)]'}
          ${isOpen ? 'border-[var(--accent-strong)]' : ''}
        `}
        {...props as any}
      >
        <span className="truncate">{displayValue}</span>
        <svg 
          className={`w-4 h-4 ml-2 transition-transform duration-200 shrink-0 ${isOpen ? 'rotate-180' : ''}`} 
          fill="none" 
          stroke="currentColor" 
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="square" strokeLinejoin="miter" strokeWidth="2" d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {isOpen && !disabled && (
        <div className="absolute z-[200] w-full mt-2 bg-[var(--surface-2)] border border-[var(--border-strong)] shadow-[4px_4px_0_0_var(--border-strong)] max-h-80 overflow-y-auto min-w-max">
          {hasGroups ? (
            <div className="py-1">
              {ungroupedOptions.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  className={`w-full text-left px-3 py-2 text-xs font-bold uppercase tracking-widest hover:bg-[var(--surface-3)] transition-colors ${value === option.value ? 'text-[var(--accent-strong)] bg-[var(--surface-1)]' : 'text-[var(--app-text)]'}`}
                  onClick={() => handleSelect(option.value)}
                >
                  {option.label}
                </button>
              ))}
              {Object.entries(groupedOptions).map(([group, opts]) => (
                <div key={group} className="mt-2">
                  <div className="px-3 py-1 text-[10px] font-bold uppercase tracking-[0.2em] text-[var(--app-text-muted)] border-y border-[var(--border-subtle)] bg-[var(--surface-1)]">
                    {group}
                  </div>
                  {opts.map((option) => (
                    <button
                      key={option.value}
                      type="button"
                      className={`w-full text-left px-4 py-2 text-xs font-bold uppercase tracking-widest hover:bg-[var(--surface-3)] transition-colors ${value === option.value ? 'text-[var(--accent-strong)] bg-[var(--surface-1)]' : 'text-[var(--app-text)]'}`}
                      onClick={() => handleSelect(option.value)}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              ))}
            </div>
          ) : (
            <div className="py-1">
              {options.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  className={`w-full text-left px-3 py-2 text-xs font-bold uppercase tracking-widest hover:bg-[var(--surface-3)] transition-colors ${value === option.value ? 'text-[var(--accent-strong)] bg-[var(--surface-1)]' : 'text-[var(--app-text)]'}`}
                  onClick={() => handleSelect(option.value)}
                >
                  {option.label}
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default Select;